import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ImageIcon, MapPin, TrendingUp, Package } from "lucide-react";

const HERO_LABELS = { miami: "Miami sunset", vegas: "Vegas neon", unknown: "Unknown" };
const PRODUCT_LABELS = { "cherry-bomb": "Cherry Bomb", "freshly-baked": "Freshly Baked" };

export default function HeroesPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [productData, setProductData] = useState(null);
  const [productRange, setProductRange] = useState("30d");

  useEffect(() => {
    api.get(`/admin/heroes?days=${days}`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch((err) => console.error("Hero report failed:", err));
  }, [days]);

  useEffect(() => {
    api.get("/hero-product/report", { withCredentials: true })
      .then((r) => setProductData(r.data))
      .catch((err) => console.error("Hero product report failed:", err));
  }, []);

  if (!data) return <div className="text-white/50 text-sm">Loading hero data…</div>;

  const productRows = productData?.ranges?.[productRange] || {};

  return (
    <section data-testid="heroes-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-white">Hero Variants</h2>
          <div className="text-white/40 text-xs mt-1">
            Which cities see which hero + which one converts best
          </div>
        </div>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`heroes-range-${d}`}
              className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full transition-colors ${
                days === d
                  ? "bg-[#FF3D8B] text-black font-bold"
                  : "border border-white/15 text-white/70 hover:border-white/40"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-5">
          <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-widest mb-2">
            <ImageIcon size={14} className="text-[#FF3D8B]" /> Visits w/ Hero
          </div>
          <div className="font-display text-3xl text-white">{data.total_visits}</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-5">
          <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-widest mb-2">
            <TrendingUp size={14} className="text-[#A855F7]" /> Orders
          </div>
          <div className="font-display text-3xl text-white">{data.total_orders}</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-5">
          <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-widest mb-2">
            <TrendingUp size={14} className="text-[#FF3D8B]" /> Revenue
          </div>
          <div className="font-display text-3xl text-white">${data.total_revenue.toFixed(2)}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-white/10 bg-[#0f0f0f] overflow-hidden">
          <div className="p-5 border-b border-white/10 text-white/60 text-xs uppercase tracking-widest">
            Hero performance
          </div>
          <table className="w-full text-sm">
            <thead className="text-white/50 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left p-4">Hero</th>
                <th className="text-left p-4">Visits</th>
                <th className="text-left p-4">Orders</th>
                <th className="text-left p-4">CR</th>
                <th className="text-left p-4">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {data.hero_rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-white/40 text-sm">
                    No hero impressions tracked yet.
                  </td>
                </tr>
              ) : (
                data.hero_rows.map((h) => (
                  <tr key={h.hero} className="border-t border-white/5" data-testid={`hero-row-${h.hero}`}>
                    <td className="p-4">
                      <div className="font-semibold text-white">{HERO_LABELS[h.hero] || h.hero}</div>
                      <div className="text-white/40 text-xs mt-0.5">
                        top: {h.top_cities.slice(0, 3).map((c) => c.city).join(", ") || "—"}
                      </div>
                    </td>
                    <td className="p-4 text-white">{h.visits}</td>
                    <td className="p-4 text-white">{h.orders}</td>
                    <td className="p-4 text-white/70">{h.conversion_rate}%</td>
                    <td className="p-4 text-[#FF3D8B] font-semibold">${h.revenue.toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="rounded-xl border border-white/10 bg-[#0f0f0f] overflow-hidden">
          <div className="p-5 border-b border-white/10 text-white/60 text-xs uppercase tracking-widest flex items-center gap-2">
            <MapPin size={13} className="text-[#A855F7]" /> Top cities → winning hero
          </div>
          <table className="w-full text-sm">
            <thead className="text-white/50 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left p-4">City</th>
                <th className="text-left p-4">Visits</th>
                <th className="text-left p-4">Winning hero</th>
              </tr>
            </thead>
            <tbody>
              {data.city_rows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="p-6 text-center text-white/40 text-sm">
                    Not enough traffic yet to break down by city.
                  </td>
                </tr>
              ) : (
                data.city_rows.map((c) => (
                  <tr key={c.city} className="border-t border-white/5" data-testid={`city-row-${c.city}`}>
                    <td className="p-4 text-white/90">{c.city}</td>
                    <td className="p-4 text-white/70">{c.visits}</td>
                    <td className="p-4">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#FF3D8B]/20 text-[#FF3D8B]">
                        {HERO_LABELS[c.top_hero] || c.top_hero} · {c.top_hero_visits}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8 rounded-xl border border-white/10 bg-[#0f0f0f] overflow-hidden" data-testid="hero-product-report">
        <div className="p-5 border-b border-white/10 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-widest">
            <Package size={13} className="text-[#FF3D8B]" /> Home Product A/B — Cherry Bomb vs Freshly Baked
          </div>
          <div className="flex items-center gap-3">
            {productData && (
              <span className="text-[10px] uppercase tracking-widest text-white/40" data-testid="hero-product-today">
                Today: <span className="text-[#FF3D8B]">{PRODUCT_LABELS[productData.today_variant] || productData.today_variant}</span>
              </span>
            )}
            <div className="flex gap-1">
              {["24h", "7d", "30d"].map((k) => (
                <button
                  key={k}
                  onClick={() => setProductRange(k)}
                  data-testid={`hero-product-range-${k}`}
                  className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full transition-colors ${
                    productRange === k
                      ? "bg-[#FF3D8B] text-black font-bold"
                      : "border border-white/15 text-white/70 hover:border-white/40"
                  }`}
                >
                  {k}
                </button>
              ))}
            </div>
          </div>
        </div>
        {!productData ? (
          <div className="p-6 text-white/40 text-sm">Loading product report…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-white/50 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left p-4">Product</th>
                <th className="text-left p-4">Views</th>
                <th className="text-left p-4">Clicks</th>
                <th className="text-left p-4">CTR</th>
                <th className="text-left p-4">Conversions</th>
                <th className="text-left p-4">CVR</th>
                <th className="text-left p-4">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(productRows).length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-white/40 text-sm">
                    No hero-product events tracked yet — visit the home page to seed the first view.
                  </td>
                </tr>
              ) : (
                Object.entries(productRows).map(([variant, m]) => (
                  <tr key={variant} className="border-t border-white/5" data-testid={`hero-product-row-${variant}`}>
                    <td className="p-4">
                      <div className="font-semibold text-white">{PRODUCT_LABELS[variant] || variant}</div>
                    </td>
                    <td className="p-4 text-white">{m.views}</td>
                    <td className="p-4 text-white">{m.clicks}</td>
                    <td className="p-4 text-white/70">{m.ctr}%</td>
                    <td className="p-4 text-white">{m.conversions}</td>
                    <td className="p-4 text-white/70">{m.conversion_rate}%</td>
                    <td className="p-4 text-[#FF3D8B] font-semibold">${m.revenue.toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
        <div className="p-4 text-white/40 text-[11px] border-t border-white/5">
          Featured product rotates every UTC day (deterministic — every visitor sees the same
          product on the same day). Clicking the floating freshener on the home page deep-links
          to that product's PDP. Conversions are attributed by cart_id to the last hero view.
        </div>
      </div>

      <div className="mt-6 text-white/40 text-xs">
        Hero variant is chosen by URL slug on geo pages (miami-fl → Miami, las-vegas-nv → Vegas)
        and by IP geolocation everywhere else. Cached per session.
      </div>
    </section>
  );
}
